/**
 * ============ LANE 3 ACTIVE PRODUCTION SURFACE ============
 * DO NOT DELETE. Triggered by SelfReportDialog.onMultipleConfirmed after an
 * unverified multi-performer upload. Renders the "Verify via Easy2257" CTA
 * (line ~300) that links to /integration/easy2257?recordId={id}.
 * The CTA is gated behind GET /api/integration/easy2257/enabled (2026-08-14):
 * the flow it starts posts to EZ2257 /v1/verifications, which EZ2257 removed
 * 2026-04-25, so the button is hidden for everyone outside the Easy2257
 * test allowlist until the modern upload-session flow replaces it.
 * Manual performer tagging in this modal is unaffected by the gate.
 * Active call sites:
 *   - app/profile/videos/create/page.tsx
 *   - app/profile/galleries/edit/[galleryId]/page.tsx
 *   - app/gallery/[galleryId]/page.tsx
 * See vault/projects/juicystarz/ez2257-juicystarz-integration-review-2026-05-21.md Addendum 2.
 * Last reviewed: 2026-08-14.
 * ==========================================================
 */
"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { apiFetch, apiGet } from "@/lib/api";
import { ComplianceStatusBadge } from "@/components/compliance/ComplianceStatusBadge";
import { X, Search, UserPlus, ExternalLink } from "lucide-react";

type SearchResult = {
  id: number;
  aspNetUserId: string;
  stageName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isVerified: boolean;
  avatarUrl: string | null;
};

type TaggedPerformer = {
  id: number;
  performerName: string;
  personId: number | null;
  userId: string | null;
  ageVerificationStatus: string;
  modelReleaseObtained: boolean;
};

interface PerformerTaggingModalProps {
  content2257RecordId: number;
  isOpen: boolean;
  onClose: () => void;
  onTagged: () => void;
}

export function PerformerTaggingModal({
  content2257RecordId,
  isOpen,
  onClose,
  onTagged,
}: PerformerTaggingModalProps) {
  const { data: session } = useSession();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [tagging, setTagging] = useState<number | null>(null);
  const [tagged, setTagged] = useState<TaggedPerformer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ezEnabled, setEzEnabled] = useState(false);

  // Load existing performers
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await apiGet<TaggedPerformer[]>(
          `/api/content/${content2257RecordId}/performers`,
          session
        );
        setTagged(data || []);
      } catch {
        /* ignore */
      }
    })();
  }, [isOpen, content2257RecordId, session]);

  // Probe whether the Easy2257 lane is enabled for this user. Hidden by
  // default: the CTA targets a flow EZ2257 removed, so only allowlisted
  // test accounts should ever see it (see header comment).
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const ez = await apiGet<{ enabled: boolean }>(
          `/api/integration/easy2257/enabled`,
          session
        );
        setEzEnabled(Boolean(ez?.enabled));
      } catch {
        setEzEnabled(false);
      }
    })();
  }, [isOpen, session]);

  // Debounced search
  const searchUsers = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const data = await apiGet<any>(
          `/api/person/autocomplete?q=${encodeURIComponent(q)}&take=10`,
          session
        );
        const rawItems = Array.isArray(data) ? data : data?.items || data?.results || [];
        const items: SearchResult[] = rawItems.map((p: any) => ({
          id: p.id ?? p.personId ?? p.Id,
          aspNetUserId: p.aspNetUserId ?? p.AspNetUserId ?? "",
          stageName: p.stageName ?? p.StageName ?? null,
          firstName: p.firstName ?? p.FirstName ?? null,
          lastName: p.lastName ?? p.LastName ?? null,
          email: p.email ?? p.Email ?? null,
          isVerified: Boolean(
            p.accountverified ?? p.isVerified ?? p.ageVerified
          ),
          avatarUrl: p.avatarUrl ?? p.profileImageLocation ?? null,
        }));
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [session]
  );

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(query), 300);
    return () => clearTimeout(timer);
  }, [query, searchUsers]);

  async function tagPerformer(person: SearchResult) {
    setTagging(person.id);
    setError(null);
    try {
      const resp = await apiFetch(
        `/api/content/${content2257RecordId}/performers`,
        session,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            personId: person.id,
            userId: person.aspNetUserId,
            email: person.email,
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.error || `Failed (${resp.status})`);
      }
      const newPerformer = await resp.json();
      setTagged((prev) => [...prev, newPerformer]);
      setQuery("");
      setResults([]);
      onTagged();
    } catch (e: any) {
      setError(e?.message || "Failed to tag performer");
    } finally {
      setTagging(null);
    }
  }

  async function removePerformer(performerId: number) {
    try {
      await apiFetch(
        `/api/content/${content2257RecordId}/performers/${performerId}`,
        session,
        { method: "DELETE" }
      );
      setTagged((prev) => prev.filter((p) => p.id !== performerId));
      onTagged();
    } catch {
      /* ignore */
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-background rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Tag Performers</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users by name..."
              className="w-full pl-9 pr-3 py-2 rounded-md border bg-background text-sm"
              autoFocus
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {/* Search Results */}
          {searching && (
            <p className="text-sm text-muted-foreground">Searching...</p>
          )}
          {results.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">
                Search Results
              </p>
              {results.map((person) => {
                const alreadyTagged = tagged.some(
                  (t) => t.personId === person.id
                );
                return (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {person.stageName ||
                          `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
                          "Unknown"}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {person.isVerified ? (
                          <span className="text-xs text-green-600 font-medium">
                            ID Verified
                          </span>
                        ) : (
                          <span className="text-xs text-yellow-600 font-medium">
                            Not Verified
                          </span>
                        )}
                        {person.email && (
                          <span className="text-xs text-muted-foreground truncate">
                            {person.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => tagPerformer(person)}
                      disabled={alreadyTagged || tagging === person.id || !person.isVerified}
                      title={!person.isVerified ? "Only ID-verified members can be tagged" : undefined}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {alreadyTagged ? (
                        "Tagged"
                      ) : !person.isVerified ? (
                        "Not Verified"
                      ) : tagging === person.id ? (
                        "Adding..."
                      ) : (
                        <>
                          <UserPlus className="w-3.5 h-3.5" />
                          Tag
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tagged Performers */}
          {tagged.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">
                Tagged Performers ({tagged.length})
              </p>
              {tagged.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{p.performerName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <ComplianceStatusBadge
                        status={p.ageVerificationStatus}
                      />
                      {p.modelReleaseObtained ? (
                        <span className="text-xs text-green-600">
                          Release Signed
                        </span>
                      ) : (
                        <span className="text-xs text-yellow-600">
                          Release Pending
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removePerformer(p.id)}
                    className="shrink-0 p-1.5 rounded-md hover:bg-red-100 text-red-600"
                    title="Remove performer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Easy2257 Option — allowlisted test accounts only, see header comment */}
          {ezEnabled && (
            <div className="p-3 rounded-lg border border-dashed border-blue-300 bg-blue-50 dark:bg-blue-900/20">
              <p className="text-sm font-medium mb-1">
                People not on JuicyStarz?
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Use Easy2257 for third-party performer compliance verification.
              </p>
              <a
                href={`/integration/easy2257?recordId=${content2257RecordId}`}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Verify via Easy2257
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-md border bg-background hover:bg-accent text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default PerformerTaggingModal;
